import React, { useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import {
  Search,
  Upload,
  Bell,
  Menu,
  Shield,
  Heart,
  MessageSquare,
  FileText
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { AppAbility } from "@/lib/ability";
import { fetchUnseenAlertCount } from "@/data-access/alerts";

const getNavItems = (unseenCount: number) => [
  { label: "Search", icon: Search, to: "/search", visible: () => true },
  { label: "Batch Import", icon: Upload, to: "/batch-import", visible: () => true },
  { label: "Favorites", icon: Heart, to: "/favorites", visible: () => true },
  { label: "Alert", icon: Bell, to: "/alerts", visible: () => true, badge: unseenCount > 0 ? unseenCount : undefined },
  { label: "Feedback", icon: MessageSquare, to: "/feedback", visible: () => true },
  { label: "Admin", icon: Shield, to: "/admin", visible: (ability: AppAbility) => ability.can('read', 'admin_page') },
  { label: "Legal", icon: FileText, to: "/legal", visible: () => true },
];

function SidebarItem({ icon: Icon, label, to, active, badge }: any) {
  return (
    <Link href={to} className="block">
      <div
        className={cn(
          "w-full flex items-center text-left px-4 py-3 transition-all duration-200 rounded-lg text-sm font-medium relative",
          active
            ? "bg-primary text-primary-foreground shadow-sm"
            : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        )}
      >
        <Icon className={cn("h-5 w-5 mr-3", active ? "text-primary-foreground" : "text-primary")} />
        <span className="flex-grow">{label}</span>
        {badge && (
          <span className="absolute right-2 h-5 w-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </div>
    </Link>
  );
}

export default function Sidebar() {
  const [location, setLocation] = useLocation();
  const [expanded, setExpanded] = useState(false);
  const { ability } = useAuth();

  // Use react-query to manage unseen count
  const { data: unseenCount = 0 } = useQuery({
    queryKey: ['/api/wallet-alerts/history/unseen-count'],
    queryFn: async () => {
      const res = await fetchUnseenAlertCount();
      return res.count;
    },
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true,
  });

  const navItems = getNavItems(unseenCount);

  const toggleSidebar = () => setExpanded((v) => !v);

  return (
    <>
      <Button
        variant="outline"
        size="icon"
        onClick={toggleSidebar}
        className="fixed left-4 top-4 z-40 md:hidden"
      >
        <Menu className="h-4 w-4 text-primary" />
      </Button>
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 h-screen bg-sidebar transform transition-transform duration-300 ease-in-out",
          expanded ? "translate-x-0" : "-translate-x-full",
          "md:translate-x-0"
        )}
      >
        <div className="h-full flex flex-col">
          {/* Header with BLOCKSEER branding */}
          <div className="px-6 py-6">
            <div className="flex items-center justify-center gap-2">
              <Link href="/">
                <img src="/assets/Blockseer-logo-blue.png" alt="Blockseer Logo" className="h-10" />
              </Link>
            </div>
          </div>

          {/* Navigation Items */}
          <div className="flex-grow overflow-y-auto px-4 py-4">
            <div className="space-y-1">
              {navItems.filter((item) => item.visible ? item.visible(ability) : true).map((item) => (
                <SidebarItem
                  key={item.to}
                  icon={item.icon}
                  label={item.label}
                  to={item.to}
                  active={location === item.to}
                  badge={item.badge}
                />
              ))}
            </div>
          </div>

          {/* DMG Logo Section */}
          <div className="px-4 py-4">
            <div className="flex justify-center mb-2">
              <a 
                href="https://www.dmgblockchain.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block hover:opacity-80 transition-opacity duration-200"
              >
                <img 
                  src="/assets/dmg_logo.jpeg" 
                  alt="DMG Blockchain" 
                  className="w-auto rounded-lg"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
      {expanded && (
        <div
          className="fixed inset-0 bg-black/30 z-40 md:hidden"
          onClick={() => setExpanded(false)}
        />
      )}
    </>
  );
} 